import abslog from 'abslog';
import type { ValidLogger } from 'abslog';
import type {
  Fetch,
  SearchClientRequestOptions,
  SearchClientResponse,
} from './types';
import type {
  SearchKey,
  SearchResult,
  Sort,
  SubVertical,
  Vertical,
} from './types/quest';

export * from './types/quest';

export type QuestRequestOptions = SearchClientRequestOptions & {
  abTestKeys?: string[];
  finnToken?: string;
  searchParams?: URLSearchParams;
};

export type QuestClientOptions = {
  baseUrl: string;
  clientName: string;
  fetchFn: Fetch;
  logger?: ValidLogger;
};

export class QuestClient {
  private baseUrl: string;
  private clientName: string;
  private fetchFn: Fetch;
  private logger: ValidLogger;

  constructor(options: QuestClientOptions) {
    this.baseUrl = options.baseUrl;
    this.clientName = options.clientName;
    this.fetchFn = options.fetchFn;
    this.logger = abslog(options.logger);
  }

  filters(
    request: { searchKey: SearchKey } & QuestRequestOptions,
  ): Promise<SearchClientResponse<SearchResult>> {
    const { searchKey } = request;
    const path = `/filters/${searchKey}`;
    return this.fetch<SearchResult>(path, request);
  }

  heatmap(
    request: { searchKey: SearchKey } & QuestRequestOptions,
  ): Promise<SearchClientResponse<SearchResult>> {
    const { searchKey } = request;
    const path = `/heatmap/${searchKey}`;
    return this.fetch<SearchResult>(path, request);
  }

  map(
    request: { searchKey: SearchKey } & QuestRequestOptions,
  ): Promise<SearchClientResponse<SearchResult>> {
    const { searchKey } = request;
    const path = `/map/${searchKey}`;
    return this.fetch<SearchResult>(path, request);
  }

  org(
    request: { searchKey?: SearchKey } & QuestRequestOptions,
  ): Promise<SearchClientResponse<SearchResult>> {
    const { searchKey } = request;
    const path = `/org${searchKey ? `/${searchKey}` : ''}`;
    return this.fetch<SearchResult>(path, request);
  }

  profile(
    request: { searchKey?: SearchKey } & QuestRequestOptions,
  ): Promise<SearchClientResponse<SearchResult>> {
    const { searchKey } = request;
    const path = `/profile${searchKey ? `/${searchKey}` : ''}`;
    return this.fetch<SearchResult>(path, request);
  }

  search(
    request: { searchKey: SearchKey } & QuestRequestOptions,
  ): Promise<SearchClientResponse<SearchResult>> {
    const { searchKey } = request;
    const path = `/search/${searchKey}`;
    return this.fetch<SearchResult>(path, request);
  }

  sort(
    request: { searchKey: SearchKey } & QuestRequestOptions,
  ): Promise<SearchClientResponse<Sort[]>> {
    const { searchKey } = request;
    const path = `/sort/${searchKey}`;
    return this.fetch<Sort[]>(path, request);
  }

  subverticals(
    request: { vertical: Vertical } & QuestRequestOptions,
  ): Promise<SearchClientResponse<SubVertical[]>> {
    const { vertical } = request;
    const path = `/subverticals/${vertical}`;
    return this.fetch<SubVertical[]>(path, request);
  }

  private async fetch<TData>(
    path: string,
    requestOptions: QuestRequestOptions,
  ): Promise<SearchClientResponse<TData>> {
    const { searchParams, requestInit, ...options } = requestOptions;
    const url = this.getUrl(path, searchParams);
    const headers = this.getHeaders(options);

    const promise = this.fetchFn(url, {
      ...requestInit,
      headers,
    });

    return promise as Promise<SearchClientResponse<TData>>;
  }

  private getUrl(path: string, searchParams?: URLSearchParams): URL {
    const urlParams = searchParams ?? new URLSearchParams();
    urlParams.set('client', this.clientName);

    const url = new URL(path, this.baseUrl);
    url.search = urlParams.toString();

    return url;
  }

  private getHeaders(options: QuestRequestOptions) {
    const headers: HeadersInit = {
      Accept: 'application/json',
    };

    const { abTestKeys, finnToken } = options;

    if (abTestKeys) {
      const invalidKeys = abTestKeys.filter(
        (key) => encodeURIComponent(key) !== key,
      );
      if (invalidKeys.length > 0) {
        invalidKeys.forEach((key) =>
          this.logger.warn(`Invalid abTestKey ${key}`),
        );
      }

      const validKeys = abTestKeys.filter(
        (key) => encodeURIComponent(key) === key,
      );
      if (validKeys.length > 0) {
        headers['abTestKeys'] = validKeys.join(',');
      }
    }

    if (finnToken) {
      headers['Authorization'] = finnToken;
    }

    return headers;
  }
}
